import { useEffect } from 'react';
import { certificationActions } from 'store/slices/certification/certificationSilce';

/**
 * @hook useEpstLifecycle
 * @description
 * Hook personalizado que gestiona el ciclo de vida del examen EPST. Controla:
 * - El comportamiento del temporizador según el `step`.
 * - La inicialización de preguntas y control del estado de avance.
 * - La lógica de envío automático cuando se termina el tiempo (`timeOver`).
 *
 * @param {Object} params
 * @param {number} params.step - Paso actual del examen (bienvenida, sección, descanso, final).
 * @param {boolean} params.timeOver - Indica si se agotó el tiempo del temporizador.
 * @param {Object|null} params.selectedAnswer - Respuesta actual del usuario.
 * @param {Array} params.questions - Lista de preguntas actuales en la sección.
 * @param {Function} params.setCurrentQuestion - Setter para actualizar la pregunta actual.
 * @param {Function} params.dispatch - Función dispatch de Redux para actualizar el estado del examen.
 * @param {Function} params.handleSubmitAnswer - Función para enviar una respuesta válida.
 * @param {Function} params.handleSubmitNoAnswer - Función para enviar una respuesta vacía.
 */
const useEpstLifecycle = ({
	step,
	timeOver,
	selectedAnswer,
	questions,
	setCurrentQuestion,
	dispatch,
	handleSubmitAnswer,
	handleSubmitNoAnswer
}) => {
	/**
	 * Maneja la configuración del temporizador dependiendo del paso del examen.
	 * - Bienvenida y final → temporizador apagado.
	 * - Descansos → temporizador de 10 minutos (600s).
	 */
	useEffect(() => {
		if ([1, 11].includes(step)) {
			dispatch(certificationActions.setInitialTime(0));
			dispatch(certificationActions.setStartTimer(false));
			dispatch(certificationActions.setShowTimer(false));
		} else if ([3, 5, 7, 9].includes(step)) {
			dispatch(certificationActions.setInitialTime(600));
			dispatch(certificationActions.setStartTimer(true));
			dispatch(certificationActions.setShowTimer(true));
		}
	}, [step]);

	/**
	 * Cuando se cargan preguntas, configura la primera pregunta como actual
	 * y ajusta el tiempo disponible basado en la metadata de la pregunta.
	 * Si no hay preguntas, avanza automáticamente al siguiente paso.
	 */
	useEffect(() => {
		if (questions === null) return;
		if (questions.length > 0) {
			setCurrentQuestion({ ...questions[0] });
			dispatch(certificationActions.setInitialTime(questions[0].time));
			dispatch(certificationActions.setStartTimer(true));
			dispatch(certificationActions.setShowTimer(true));
		} else {
			dispatch(certificationActions.resetSection());
			dispatch(certificationActions.incrementStep());
			setCurrentQuestion({});
		}
	}, [questions]);

	/**
	 * Cuando se termina el tiempo (`timeOver`), determina si debe enviarse una respuesta
	 * vacía o válida, dependiendo del estado de `selectedAnswer`.
	 */
	useEffect(() => {
		const handleTimeOver = async () => {
			if (!questions || questions.length === 0) return;
			if (!selectedAnswer || Object.keys(selectedAnswer).length === 0) {
				handleSubmitNoAnswer();
			} else {
				handleSubmitAnswer();
			}
		};
		if (timeOver && questions?.length > 0) handleTimeOver();
	}, [timeOver]);
};

export default useEpstLifecycle;